/**
 * useOrderDetails Hook
 *
 * Reads the completed order data for the post-purchase page.
 *
 * Order ID resolution priority:
 *   1. URL search param `order_id` — set by checkout app on redirect
 *   2. URL search param `orderId`  — alternate key used by thank-you page
 *
 * Mock orders (id prefixed with `mock_order_`) skip the API call and return
 * empty data so the local mock-payment flow still reaches /post-purchase.
 *
 * Fail-open: if the API call fails, the hook resolves with empty arrays/zeros
 * so the evaluate-step call still fires without order context.
 */

'use client';

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getOrderService } from '@/infrastructure/services/order.service';
import { storefrontLayoutLogger } from '@/lib/logger';

const MOCK_ORDER_PREFIX = 'mock_order_';

export interface OrderDetails {
  /** Completed order ID resolved from URL params. */
  orderId: string | null;
  /** Product IDs from the completed order's line items. */
  orderProductIds: string[];
  /** Order grand total in dollars (display currency). */
  orderTotal: number;
  /** True while the order fetch is in-flight. */
  loading: boolean;
}

/**
 * Converts a cents value (integer) to dollars.
 * Defensively handles non-numeric input by returning 0.
 */
function centsToDollars(cents: number): number {
  if (!Number.isFinite(cents) || cents < 0) return 0;
  return cents / 100;
}

export function useOrderDetails(): OrderDetails {
  const [searchParams] = useSearchParams();

  // Accept both ?order_id= (checkout app convention) and ?orderId= (thank-you page convention)
  const orderId = searchParams.get('order_id') ?? searchParams.get('orderId');

  const [orderProductIds, setOrderProductIds] = useState<string[]>([]);
  const [orderTotal, setOrderTotal] = useState<number>(0);
  // Start loading=true immediately when an orderId is present so consumers
  // (PostPurchasePage) don't race past the loading gate before the fetch fires.
  const [loading, setLoading] = useState<boolean>(!!orderId);

  useEffect(() => {
    if (!orderId) {
      // No order ID in URL — nothing to fetch, resolve immediately
      setOrderProductIds([]);
      setOrderTotal(0);
      setLoading(false);
      return;
    }

    if (orderId.startsWith(MOCK_ORDER_PREFIX)) {
      // Mock payment mode — skip API call, return empty data
      if (import.meta.env.DEV) {
        storefrontLayoutLogger.info('[useOrderDetails] mock order detected; skipping API fetch', {
          orderId,
        });
      }
      setOrderProductIds([]);
      setOrderTotal(0);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    async function fetchOrder(): Promise<void> {
      try {
        const order = await getOrderService().getOrderById(orderId as string);

        if (cancelled) return;

        const productIds = (order.line_items ?? []).map(
          (item) => item.product.product_id,
        );
        const totalCents = order.totals?.total?.display ?? 0;

        setOrderProductIds(productIds);
        setOrderTotal(centsToDollars(totalCents));
      } catch (err) {
        if (cancelled) return;
        // Fail-open: log the error but resolve with empty data so evaluate-step
        // can still fire without order context rather than blocking the upsell page.
        storefrontLayoutLogger.warn('[useOrderDetails] order fetch failed; proceeding without order data', {
          orderId,
          error: err instanceof Error ? err.message : String(err),
        });
        setOrderProductIds([]);
        setOrderTotal(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchOrder();
    return () => { cancelled = true; };
  }, [orderId]);

  return { orderId, orderProductIds, orderTotal, loading };
}
